import {log} from "@/lib/Logger";
import Campus from "../types/Campus";
import getLegacySitePath from "./getLegacySitePath";
import {Class} from "../../lib/legacyApi/fetchClassesForCourseByCampusId";
import {AbbreviatedLegacyClass} from "../../shared/legacyTypes/fetchAllClassesForCampusTypes";
import EnrollStatus from "../types/EnrollStatus";

export const removeTrailingSlash = (path: string) => {
	if (path.endsWith("/")) {
		return path.slice(0, -1);
	}

	return path;
};

export const NEXT_SITE_URL = removeTrailingSlash(
	process.env.NEXT_PUBLIC_NEXT_SITE_URL ?? ""
);

export const NEXT_SITE_URL_OBJECT = new URL(
	process.env.NEXT_PUBLIC_NEXT_SITE_URL ?? "https://aopsacademy.org"
);

export const isSameNextAppPath = (path1: string, path2: string) => {
	/**
	 * use URL constructor with NEXT_PUBLIC_NEXT_SITE_URL base because we don't
	 * know if href is a fragment.
	 **/
	const nextBaseURL = process.env.NEXT_PUBLIC_NEXT_SITE_URL;
	if (!nextBaseURL) {
		log.warn(
			{
				module: "app/util/urlUtils.ts",
				fn: "isSameNextAppPath",
				label: "NoNextSiteUrl",
			},
			"No env value found for NEXT_PUBLIC_NEXT_SITE_URL"
		);
	}

	const nextUrl = new URL(nextBaseURL ?? "http://localhost");
	const currentLocation = new URL(path1, nextUrl);
	const hrefLocation = new URL(path2, nextUrl);

	return (
		currentLocation.host === hrefLocation.host &&
		removeTrailingSlash(currentLocation.pathname) ===
			removeTrailingSlash(hrefLocation.pathname)
	);
};

function encodeURIPath(redirectPath: string) {
	if (redirectPath && !redirectPath.startsWith("/")) {
		redirectPath = "/" + redirectPath;
	}
	return encodeURIComponent(`${NEXT_SITE_URL}${redirectPath}`);
}

export function getSignInUrl(redirectPath: string) {
	const redirect = encodeURIPath(redirectPath);
	return `${NEXT_SITE_URL}/sign-in?redirect=${redirect}`;
}

export function getSignOutUrl(redirectPath: string) {
	const redirect = encodeURIPath(redirectPath);
	return `${NEXT_SITE_URL}/sign-out?redirect=${redirect}`;
}

export function getCampusTuitionPath(campus: Campus) {
	return `/campus/${campus.campusId}/tuition`;
}

export function getCampusSchedulePath(campus: Campus) {
	return `/campus/${campus.campusId}/class-schedule`;
}

export function getCoursePath(courseId: string) {
	return `/courses/course/${courseId}`;
}

/**
 * Make the href used by the "Add another student" link.
 *
 * The query params depend on the class's enroll status.
 *
 * @param classToAdd The class to add a student to
 */
export function makeRegistrationHref(
	classToAdd: Class | AbbreviatedLegacyClass
) {
	const registrationParams = new URLSearchParams();
	if (classToAdd.enrollStatus === EnrollStatus.WAITLIST) {
		registrationParams.set("waitlist", classToAdd.classId);
	} else if (classToAdd.enrollStatus === EnrollStatus.OPEN) {
		registrationParams.set("class", classToAdd.classId);
	}
	const addNewStudentHref = getLegacySitePath(
		`/admissions/registration?${registrationParams}`,
		classToAdd.campusId
	);

	return addNewStudentHref;
}
